import React, { useState, useEffect, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Link from '@atlaskit/link';
import Lozenge from '@atlaskit/lozenge';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { getOpsgenieInstanceUrl } from '@atlassian/jira-cross-flow-utils/src/services/index.tsx';
import { useOgTenantInfo } from '@atlassian/jira-eoc-tenant-info/src/controllers/index.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { useFlagsService, toFlagId, type FlagId } from '@atlassian/jira-flags';
import WarningFlag from '@atlassian/jira-flags/src/common/ui/components/warning-flag';
import { FormattedI18nMessage } from '@atlassian/jira-formatted-i18n-message/src/ui/index.tsx';
import MajorIncidentsIcon from '@atlassian/jira-icons/src/ui/jsm-major-incident';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useRestoreFields } from '@atlassian/jira-servicedesk-custom-fields/src/services/use-restore-fields/index.tsx';
import majorIncidentImage from '../../../assets/major-incident.svg';
import { MAJOR_INCIDENT_FIELD_KEY } from '../../../common/constants';
import { NewField } from '../../../common/ui/new-field';
import messages from './messages';

const MAJOR_INCIDENT_WARNING_FLAG = 'create-major-incident-field-warning-flag';

type Props = {
	isFieldMissing: boolean;
	isFlagVisible: boolean;
	setFlagVisible: (arg1: boolean) => void;
};

const defaultOpsgenieInstanceUrl = 'https://www.opsgenie.com/incident/list';

export const CreateMajorIncidentFieldWarningFlag = ({
	warningFlagId,
}: {
	warningFlagId: FlagId;
}) => {
	const { showFlag, dismissFlag } = useFlagsService();
	const { formatMessage } = useIntl();
	const [, { restoreFields }] = useRestoreFields(MAJOR_INCIDENT_FIELD_KEY);

	return (
		<WarningFlag
			id={MAJOR_INCIDENT_WARNING_FLAG}
			appearance="warning"
			title={formatMessage(messages.createMajorIncidentFieldFlagTitle)}
			description={formatMessage(messages.createMajorIncidentFieldFlagDescription)}
			actions={[
				{
					content: formatMessage(messages.createMajorIncidentFieldFlagButton),
					onClick: (_, createButtonAnalyticsEvent: UIAnalyticsEvent) => {
						fireUIAnalytics(createButtonAnalyticsEvent, 'missingMajorIncidentFieldWarningFlag');
						restoreFields(MAJOR_INCIDENT_FIELD_KEY)
							.then(() => {
								showFlag({
									type: 'success',
									title: messages.createMajorIncidentFieldSuccessFlagTitle,
									description: messages.createMajorIncidentFieldSuccessFlagDescription,
									actions: [
										{
											onClick: () => {
												window.open(
													'https://support.atlassian.com/jira-service-management-cloud/docs/what-are-major-incidents/',
													'_blank',
												);
											},
											content: messages.createMajorIncidentFieldSuccessAlertEnabledLearnMoreLink,
										},
									],
								});
								fireOperationalAnalytics(
									createButtonAnalyticsEvent,
									'missingMajorIncidentFieldCreated success',
								);
							})
							.catch(() => {
								showFlag({
									type: 'error',
									title: messages.createMajorIncidentFieldErrorFlagTitle,
									description: messages.createMajorIncidentFieldErrorFlagDescription,
								});
								fireOperationalAnalytics(
									createButtonAnalyticsEvent,
									'missingMajorIncidentFieldCreated failed',
								);
							});
						dismissFlag(warningFlagId);
					},
				},
			]}
		/>
	);
};

export const MajorIncidentField = ({ isFieldMissing, isFlagVisible, setFlagVisible }: Props) => {
	const { showFlag } = useFlagsService();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const [opsgenieUrl, setOpsgenieUrl] = useState(defaultOpsgenieInstanceUrl);

	const flagViewEvent = createAnalyticsEvent({});
	const gotoOpsgenieEvent = createAnalyticsEvent({});
	const warningFlagId = toFlagId(MAJOR_INCIDENT_WARNING_FLAG);
	const { shouldUseJSM } = useOgTenantInfo();

	useEffect(() => {
		if (isFieldMissing && !isFlagVisible) {
			setFlagVisible(true);
			showFlag({
				id: warningFlagId,
				render: () => <CreateMajorIncidentFieldWarningFlag warningFlagId={warningFlagId} />,
			});
			fireUIAnalytics(flagViewEvent, 'flag viewed', 'createMajorIncidentWarningFlag');
		}
	}, [flagViewEvent, warningFlagId, isFieldMissing, showFlag, isFlagVisible, setFlagVisible]);

	useEffect(() => {
		const getOpsgenieUrl = async (): Promise<void> => {
			const opsgenieInstanceUrl = await getOpsgenieInstanceUrl();
			setOpsgenieUrl(
				opsgenieInstanceUrl !== null
					? `${opsgenieInstanceUrl}/incident/list`
					: defaultOpsgenieInstanceUrl,
			);
		};
		getOpsgenieUrl();
	}, []);

	const descriptionLink =
		'https://support.atlassian.com/jira-service-management-cloud/docs/mark-an-incident-as-major/';

	const inProductHelpId = '1a79KwJQXHB0S0FZHMmwmy';

	return (
		<>
			<NewField
				icon={
					ff('jfe-asset-consolidation-itsm-agni_po996') ? (
						<Box xcss={MajorIncidentIconContainerStyle}>
							<MajorIncidentsIcon label="" />
						</Box>
					) : (
						<MajorIncidentIcon src={majorIncidentImage} role="presentation" />
					)
				}
				title={formatMessage(messages.majorIncidentsSubTitleHeading)}
				afterTitleContent={
					ff('im_feature_enablement_gating') ? (
						<Box xcss={premiumBadgeStyles}>
							<Lozenge appearance="new">{formatMessage(messages.premiumLozenge)}</Lozenge>
						</Box>
					) : null
				}
				description={formatMessage(messages.majorIncidentsSubTitleBody)}
				link={{
					href: descriptionLink,
					text: formatMessage(messages.majorIncidentsLearnMoreLink),
					inProductHelpArticleId: inProductHelpId,
				}}
				isFieldMissing={isFieldMissing}
				info={
					shouldUseJSM ? null : (
						<InfoBarContainer>
							<InfoIcon label="" size="medium" />
							<Box xcss={infoMessageContainerStyles}>
								<FormattedI18nMessage
									componentsMapping={{
										link: (props: { children: ReactNode }) =>
											ff('itsm-agni-25q1-a11y-fixes-_skehn') ? (
												<Link
													target="_blank"
													href={opsgenieUrl}
													onClick={() => {
														fireUIAnalytics(
															gotoOpsgenieEvent,
															'clicked gotoOpsgenieLink',
															'majorIncidentSettingsField',
														);
													}}
												>
													{props.children}
												</Link>
											) : (
												<a
													target="_blank"
													href={opsgenieUrl}
													onClick={() => {
														fireUIAnalytics(
															gotoOpsgenieEvent,
															'clicked gotoOpsgenieLink',
															'majorIncidentSettingsField',
														);
													}}
												>
													{props.children}
												</a>
											),
									}}
									message={formatMessage(messages.majorIncidentsInfo, {
										linkStart: '{linkStart}',
										linkEnd: '{linkEnd}',
									})}
								/>
							</Box>
						</InfoBarContainer>
					)
				}
			/>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MajorIncidentIcon = styled.img({
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 3}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InfoBarContainer = styled.div({
	display: 'flex',
	marginTop: token('space.250', '20px'),
});

const infoMessageContainerStyles = xcss({
	marginLeft: 'space.100',
});

const MajorIncidentIconContainerStyle = xcss({
	width: token('space.300', '24px'),
	height: token('space.300', '24px'),
});

const premiumBadgeStyles = xcss({ marginLeft: 'space.100', marginTop: 'space.025' });
